import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * The Vote Tallying Centre's reads and writes.
 *
 * ## There is no update call, and there will not be one
 *
 * A return's figures are never edited. `correctReturn` creates a NEW VERSION with a reason
 * and supersedes the old one, which stays exactly as it was declared. The absence of an
 * update endpoint is the control; a convention about not calling one is not.
 */

export interface CandidateVote {
  candidacyId: string
  ballotName: string | null
  partyShortName: string | null
  partyColourHex: string | null
  independent: boolean
  ballotOrder: number | null
  votes: number
}

export interface TallyReturn {
  id: string
  electionId: string
  seatTypeCode: string
  unitId: string
  unitName: string | null
  unitTypeCode: string | null
  unitPath: string | null
  formTypeCode: string

  registeredVoters: number
  validVotes: number
  rejectedVotes: number
  disputedVotes: number
  spoiltVotes: number
  totalCast: number
  turnoutPct: number | null
  /** More ballots cast than registered voters. Flagged, never refused. */
  overSubscribed: boolean

  statusCode: string
  versionNo: number
  supersedesReturnId: string | null
  correctionReason: string | null
  supersededAt: string | null
  sourceCode: string
  formId: string | null

  submittedBy: string | null
  submittedAt: string | null
  verifiedBy: string | null
  verifiedAt: string | null
  certifiedBy: string | null
  certifiedAt: string | null
  disputeReason: string | null
  rejectionReason: string | null

  /** As written on the paper; null when the form's lower half was never keyed. */
  presidingOfficerName: string | null
  deputyOfficerName: string | null
  /** The time ON the form. Not `submittedAt`, which is when it reached this system. */
  declaredAt: string | null
  agents: ReturnAgent[]

  candidates: CandidateVote[]
  /** Unresolved machine/human disagreements. Anything above zero blocks verification. */
  pendingVariances: number
  createdAt: string
}

/**
 * One witness from a return's declaration.
 *
 * `represents` is what the paper said; `ballotName` is what the register calls the linked
 * candidacy. Both travel — when they disagree that is itself worth seeing.
 */
export interface ReturnAgent {
  id: string
  name: string
  candidacyId: string | null
  ballotName: string | null
  partyShortName: string | null
  represents: string | null
  signed: boolean
  refusalReason: string | null
  positionNo: number
}

/** One witness as the server accepts it. */
export interface AgentEntry {
  name: string
  candidacyId?: string | null
  represents?: string | null
  /** Absent means signed — a missing flag must not record a refusal nobody made. */
  signed?: boolean
  refusalReason?: string | null
}

/**
 * The form's lower half.
 *
 * ## Omitting it and sending an empty one are different requests
 *
 * Leaving `declaration` off entirely means "not supplied" and leaves whatever is already
 * stored alone. Sending one whose `agents` is `[]` means "the form listed nobody" and
 * clears the list. A figures-only screen must omit it, never send an empty one.
 */
export interface Declaration {
  presidingOfficerName?: string | null
  deputyOfficerName?: string | null
  /** ISO instant read off the paper. */
  declaredAt?: string | null
  agents?: AgentEntry[]
}

export interface BallotEntry {
  id: string
  candidateId: string
  ballotName: string
  ballotOrder: number | null
  ballotNumber: string | null
  partyId: string | null
  partyName: string | null
  partyShortName: string | null
  partyColourHex: string | null
  independent: boolean
  seatTypeCode: string
  unitId: string
}

export interface CandidateResult {
  candidacyId: string
  ballotName: string
  partyId: string | null
  independent: boolean
  votes: number
  unitsReporting: number
  /** Of the VALID vote, never of ballots cast. */
  sharePct: number | null
}

export interface StatusCount {
  statusCode: string
  returns: number
  units: number
}

export interface SeatResult {
  electionId: string
  seatTypeCode: string
  formTypeCode: string | null
  returnsCounted: number
  unitsReporting: number
  /** null when this build cannot say how many should report. */
  unitsExpected: number | null
  registeredVoters: number
  validVotes: number
  rejectedVotes: number
  spoiltVotes: number
  disputedVotes: number
  totalCast: number
  turnoutPct: number | null
  candidates: CandidateResult[]
  /**
   * WHICH STATUSES THIS FIGURE INCLUDES. Render it — a board that does not say what it
   * counted leaves its reader unable to tell a low turnout from a slow count.
   */
  countedStatuses: string[]
  /** What the figure does NOT include, and how much of it there is. */
  statusBreakdown: StatusCount[]
}

export interface GeographyBreakdown {
  unitId: string | null
  unitName: string
  unitTypeCode: string | null
  unitPath: string
  returnsCounted: number
  unitsReporting: number
  registeredVoters: number
  validVotes: number
  rejectedVotes: number
  totalCast: number
  turnoutPct: number | null
}

export interface TallyForm {
  id: string
  electionId: string
  seatTypeCode: string
  unitId: string
  unitName: string | null
  formTypeCode: string
  fileId: string
  sha256: string | null
  ocrJobId: string | null
  statusCode: string
  meanConfidence: number | null
  uploadedBy: string | null
  uploadedAt: string
  notes: string | null
  returnId: string | null
}

export interface TallyVariance {
  id: string
  returnId: string
  fieldPath: string
  fieldLabel: string | null
  /** What the MACHINE proposed. Never overwritten. */
  ocrValue: string | null
  enteredValue: string | null
  delta: number | null
  resolutionCode: string
  resolvedBy: string | null
  resolvedAt: string | null
  resolutionNote: string | null
  unitId: string | null
  unitName: string | null
  seatTypeCode: string | null
  createdAt: string
}

export interface ReconciliationRow {
  returnId: string
  unitId: string
  unitName: string | null
  seatTypeCode: string
  formTypeCode: string
  statusCode: string
  registeredVoters: number
  validVotes: number
  rejectedVotes: number
  spoiltVotes: number
  totalCast: number
  candidateSum: number
  /** EVERY problem, not the first — a return can fail more than one way at once. */
  problems: string[]
}

export interface Figures {
  registeredVoters: number
  validVotes: number
  rejectedVotes: number
  disputedVotes: number
  spoiltVotes: number
  candidates: { candidacyId: string; votes: number }[]
}

// ── returns ────────────────────────────────────────────────────────────────────────────

export const listReturns = (p: {
  electionId: string
  seatType?: string | null
  status?: string | null
  formType?: string | null
  unitId?: string | null
  includeSuperseded?: boolean
  size?: number
  offset?: number
}) => api.get<TallyReturn[]>(`/tally/returns${qs({ ...p })}`)

export const getReturn = (id: string) => api.get<TallyReturn>(`/tally/returns/${id}`)

/** Every version for this race, newest first — what a dispute is actually argued over. */
export const returnVersions = (id: string) =>
  api.get<TallyReturn[]>(`/tally/returns/${id}/versions`)

/** The names an entry form needs boxes for, resolved from the SEAT's unit. */
export const ballotFor = (p: { electionId: string; seatType: string; unitId: string }) =>
  api.get<BallotEntry[]>(`/tally/returns/ballot${qs({ ...p })}`)

export const submitReturn = (body: {
  electionId: string
  seatTypeCode: string
  unitId: string
  formTypeCode: string
  figures: Figures
  declaration?: Declaration
  formId?: string | null
}) => api.post<TallyReturn>('/tally/returns', body)

export const verifyReturn = (id: string) =>
  api.post<TallyReturn>(`/tally/returns/${id}/verify`)

export const certifyReturn = (id: string) =>
  api.post<TallyReturn>(`/tally/returns/${id}/certify`)

export const disputeReturn = (id: string, reason: string) =>
  api.post<TallyReturn>(`/tally/returns/${id}/dispute`, { reason })

export const resolveDispute = (id: string, note?: string) =>
  api.post<TallyReturn>(`/tally/returns/${id}/resolve-dispute`, { note: note ?? null })

export const rejectReturn = (id: string, reason: string) =>
  api.post<TallyReturn>(`/tally/returns/${id}/reject`, { reason })

/**
 * The ONLY way a figure changes. Returns a DIFFERENT resource from the one in the path —
 * the return being corrected keeps exactly what it always held.
 */
export const correctReturn = (
  id: string, figures: Figures, reason: string, declaration?: Declaration,
) => api.post<TallyReturn>(`/tally/returns/${id}/correct`,
  // Omitted, not null: a correction that does not restate the declaration inherits it.
  declaration ? { figures, reason, declaration } : { figures, reason })

// ── boards ─────────────────────────────────────────────────────────────────────────────

export const liveBoard = (p: {
  electionId: string
  statuses?: string[] | null
  withinUnitId?: string | null
}) => api.get<SeatResult[]>(`/tally/board${qs({ ...p })}`)

export const seatBoard = (p: {
  electionId: string
  seatType: string
  formType?: string | null
  statuses?: string[] | null
  withinUnitId?: string | null
}) => api.get<SeatResult>(`/tally/board/seat${qs({ ...p })}`)

export const seatGeography = (p: {
  electionId: string
  seatType: string
  statuses?: string[] | null
  withinUnitId?: string | null
}) => api.get<GeographyBreakdown[]>(`/tally/board/seat/geography${qs({ ...p })}`)

export const reconciliation = (p: {
  electionId: string
  seatType?: string | null
  size?: number
}) => api.get<ReconciliationRow[]>(`/tally/board/reconciliation${qs({ ...p })}`)

// ── forms ──────────────────────────────────────────────────────────────────────────────

export const listForms = (p: {
  electionId: string
  seatType?: string | null
  status?: string | null
  unitId?: string | null
  size?: number
  offset?: number
}) => api.get<TallyForm[]>(`/tally/forms${qs({ ...p })}`)

export const getForm = (id: string) => api.get<TallyForm>(`/tally/forms/${id}`)

export const uploadForm = (body: {
  electionId: string
  seatTypeCode: string
  unitId: string
  formTypeCode: string
  fileId: string
  sha256?: string | null
  notes?: string | null
  extract?: boolean
}) => api.post<TallyForm>('/tally/forms', body)

/** 204 while nothing has been read yet — the client polls this. */
export const formExtraction = (id: string) =>
  api.get<OcrJob | null>(`/tally/forms/${id}/extraction`)

export const reExtractForm = (id: string) =>
  api.post<TallyForm>(`/tally/forms/${id}/extract`)

/**
 * Commit the reviewed figures.
 *
 * `ocrValues` is what the MACHINE proposed, keyed as `valid_votes` or `candidacy:<uuid>`.
 * Every key whose value differs from the entry becomes a permanent variance row — which is
 * why the client sends it: only the client knows which values were actually shown to the
 * human, and a variance against a value nobody saw is not a record of a decision.
 */
export const commitForm = (
  id: string, figures: Figures, ocrValues: Record<string, string>,
  declaration?: Declaration,
) => api.post<TallyReturn>(`/tally/forms/${id}/commit`,
  declaration ? { figures, ocrValues, declaration } : { figures, ocrValues })

export interface OcrJob {
  id: string
  statusCode: string
  engine: string | null
  pageCount: number | null
  meanConfidence: number | null
  rawResponse: string | null
  error: string | null
}

// ── variances ──────────────────────────────────────────────────────────────────────────

export const listVariances = (p: {
  electionId: string
  seatType?: string | null
  resolution?: string | null
  size?: number
  offset?: number
}) => api.get<TallyVariance[]>(`/tally/variances${qs({ ...p })}`)

export const variancesForReturn = (returnId: string) =>
  api.get<TallyVariance[]>(`/tally/variances/for-return/${returnId}`)

export const resolveVariance = (id: string, resolutionCode: string, note?: string) =>
  api.post<TallyVariance>(`/tally/variances/${id}/resolve`, {
    resolutionCode, note: note ?? null,
  })

// ── vocabulary ─────────────────────────────────────────────────────────────────────────

export interface Term { code: string; label: string }

/** Served rather than hard-coded, so a new code appears without a client release. */
export const tallyVocabulary = () =>
  api.get<Record<string, Term[]>>('/tally/returns/vocabulary')

// ── analytics (plan §7.3) ──────────────────────────────────────────────────────────────

export interface TallyAnalytics {
  /** What every figure below counted. Travels with the data, never assumed. */
  countedStatuses: string[]
  reporting: { hour: string; unitsReporting: number; returnsFiled: number }[]
  turnoutDistribution: { fromPct: number; toPct: number; units: number }[]
  rejectedOutliers: {
    unitId: string | null
    unitName: string
    unitPath: string
    rejectedVotes: number
    totalCast: number
    rejectedPct: number | null
  }[]
  verification: { medianSeconds: number | null; p95Seconds: number | null; verified: number }
  corrections: { unitId: string | null; unitName: string; unitPath: string; versions: number }[]
  ocr: {
    formsRead: number
    formsFailed: number
    meanConfidence: number | null
    variancesRaised: number
  }
}

export const tallyAnalytics = (p: {
  electionId: string
  seatType?: string | null
  statuses?: string[] | null
}) => api.get<TallyAnalytics>(`/tally/analytics${qs({ ...p })}`)
